import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["form", "fileInput", "report", "validateBtn", "applyBtn", "modal", "modalTitle", "warningMessage"]
  static values = { operation: String }

  connect() {
    this.shouldReloadOnClose = false
    this.modalTarget?.addEventListener('hidden.bs.modal', () => this.resetModal())
  }

  disconnect() {
    this.modalTarget?.removeEventListener('hidden.bs.modal', () => this.resetModal())
  }

  openModal(event) {
    this.operationValue = event.currentTarget.dataset.operation
    this.updateModalContent()
  }

  updateModalContent() {
    const isDelete = this.operationValue === 'delete'

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = isDelete ? 'Bulk Delete Groups' : 'Bulk Deactivate Groups'
    }

    if (this.hasWarningMessageTarget) {
      const alertClass = isDelete ? 'alert-danger' : 'alert-warning'
      const boldText = isDelete ? 'Warning:' : 'Note:'
      const message = isDelete
        ? 'This action will permanently delete the groups listed in the uploaded file. This action cannot be undone.'
        : 'This action will deactivate (set to inactive) the groups listed in the uploaded file.'

      this.warningMessageTarget.className = `alert ${alertClass}`
      this.warningMessageTarget.innerHTML = `<strong>${boldText}</strong> ${message}`
    }

    if (this.hasApplyBtnTarget) {
      this.applyBtnTarget.textContent = isDelete ? 'Delete Groups' : 'Deactivate Groups'
      this.applyBtnTarget.className = isDelete
        ? 'btn btn-danger d-none'
        : 'btn btn-warning d-none'
    }
  }

  resetModal() {
    if (this.shouldReloadOnClose) {
      window.location.reload()
      return
    }
    this.formTarget?.reset()
    if (this.reportTarget) this.reportTarget.innerHTML = ''
    this.applyBtnTarget?.classList.add('d-none')
    this.resetButton(this.validateBtnTarget, 'Validate')
  }

  onFileChange() {
    if (this.reportTarget) this.reportTarget.innerHTML = ''
    this.applyBtnTarget?.classList.add('d-none')
  }

  // Utility methods
  resetButton(button, text) {
    if (!button) return
    button.disabled = false
    button.textContent = text
  }

  setButtonLoading(button, text) {
    if (!button) return
    button.disabled = true
    button.textContent = text
  }

  scrollableDiv(content) {
    return `<div style="max-height: 300px; overflow-y: auto; margin-top: 10px;">${content}</div>`
  }

  formatRowError(rowError) {
    return rowError.row
      ? `Row ${rowError.row}: ${rowError.errors.join('; ')}`
      : rowError.errors.join('; ')
  }

  formatErrorsList(errors_by_row) {
    return errors_by_row.map(rowError => this.formatRowError(rowError)).join('<br>')
  }

  escape(str) {
    if (!str) return ''
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  validate(e) {
    e.preventDefault()
    const form = this.formTarget
    if (!form) return

    if (!this.fileInputTarget?.files?.length) {
      alert('Please select an XLSX file')
      return
    }

    const file = this.fileInputTarget.files[0]
    if (!file.name.toLowerCase().match(/\.(xlsx|xls|csv)$/)) {
      alert('Please select a valid XLSX, XLS, or CSV file')
      return
    }

    if (!this.operationValue) {
      alert('Please choose Delete or Deactivate')
      return
    }

    this.onFileChange()
    this.setButtonLoading(this.validateBtnTarget, 'Validating...')

    const baseUrl = form.action || '/admin/groups_bulk/validate'
    const url = baseUrl.match(/\.json($|\?)/) ? baseUrl : `${baseUrl}.json`

    const formData = new FormData(form)
    formData.append('operation', this.operationValue)

    fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(response => this.parseJson(response))
      .then(({ response, data }) => {
        if (response.ok) {
          this.displayReport(data)
        } else {
          this.displayUploadError(data.errors || ['Validation failed. Please try again.'])
        }
      })
      .catch(error => {
        console.error('group-bulk validate error:', error)
        alert('Validation failed. Please try again.')
      })
      .finally(() => this.resetButton(this.validateBtnTarget, 'Validate'))
  }

  displayReport(data) {
    const isDelete = this.operationValue === 'delete'
    const actionKey = isDelete ? 'to_delete' : 'to_deactivate'
    const actionLabel = isDelete ? 'Delete' : 'Deactivate'

    const actionList = data[actionKey]?.length > 0
      ? `<div class="alert alert-success">
          <strong>To ${actionLabel} (${data[actionKey].length}):</strong>
          ${this.scrollableDiv(data[actionKey].map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)})`
          ).join('<br>'))}
        </div>`
      : ''

    const notFoundList = data.not_found?.length > 0
      ? `<div class="alert alert-warning">
          <strong>Not Found (${data.not_found.length}):</strong>
          ${this.scrollableDiv(data.not_found.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const errorsList = data.errors_by_row?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(data.errors_by_row))}
        </div>`
      : ''

    const readyMessage = data.errors_count === 0 && data[actionKey]?.length > 0
      ? `<div class="alert alert-info">Validation passed! Ready to ${actionLabel.toLowerCase()} groups.</div>`
      : ''

    this.reportTarget.innerHTML = `
      ${actionList}
      ${notFoundList}
      ${errorsList}
      ${readyMessage}
    `

    this.applyBtnTarget?.classList[data[actionKey]?.length > 0 ? 'remove' : 'add']('d-none')
  }

  apply(e) {
    e.preventDefault()

    const isDelete = this.operationValue === 'delete'
    const confirmMessage = isDelete
      ? 'Are you sure you want to delete these groups? This action cannot be undone!'
      : 'Are you sure you want to deactivate these groups?'

    if (!confirm(confirmMessage)) return

    const actionLabel = isDelete ? 'Delete' : 'Deactivate'
    this.setButtonLoading(this.applyBtnTarget, `${actionLabel}ing...`)

    const formData = new FormData(this.formTarget)
    formData.append('operation', this.operationValue)

    fetch('/admin/groups_bulk/apply.json', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
    .then(response => this.parseJson(response))
    .then(({ response, data }) => {
      if (response.ok) {
        data.success ? this.displayResults(data) : this.displayErrors(data)
      } else {
        this.displayUploadError(data.errors || [data.message || `${actionLabel} failed.`])
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert(`${actionLabel} failed. Please try again.`)
    })
    .finally(() => this.resetButton(this.applyBtnTarget, `${actionLabel} Groups`))
  }

  displayResults(data) {
    const { summary, successes, failures, not_found, errors_by_row } = data
    const isDelete = this.operationValue === 'delete'
    const actionLabel = isDelete ? 'Delete' : 'Deactivate'
    const actionedLabel = isDelete ? 'Deleted' : 'Deactivated'
    const summaryCount = isDelete ? summary.deleted : summary.deactivated

    const successSection = successes?.length > 0
      ? `<div class="alert alert-success">
          <strong>${actionedLabel} (${successes.length}):</strong>
          ${this.scrollableDiv(successes.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)})`
          ).join('<br>'))}
        </div>`
      : ''

    const failedSection = failures?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Failed (${failures.length}):</strong>
          ${this.scrollableDiv(failures.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const notFoundSection = not_found?.length > 0
      ? `<div class="alert alert-warning">
          <strong>Not Found (${not_found.length}):</strong>
          ${this.scrollableDiv(not_found.map(item =>
            `Row ${item.row}: ${this.escape(item.name)} (${this.escape(item.organization_name)}) - ${this.escape(item.error)}`
          ).join('<br>'))}
        </div>`
      : ''

    const errorsSection = errors_by_row?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(errors_by_row))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-success mb-3">
        <strong>${actionLabel} Completed!</strong><br>
        ${actionedLabel}: ${summaryCount}; Failed: ${summary.failed}; Not Found: ${summary.not_found}
      </div>
      ${successSection}
      ${notFoundSection}
      ${failedSection}
      ${errorsSection}
    `

    this.applyBtnTarget?.classList.add('d-none')
    this.validateBtnTarget?.classList.remove('d-none')
    this.resetButton(this.validateBtnTarget, 'Validate')

    this.shouldReloadOnClose = true
  }

  displayErrors(data) {
    const isDelete = this.operationValue === 'delete'
    const actionLabel = isDelete ? 'Delete' : 'Deactivate'

    const errorsSection = data.errors_by_row?.length > 0
      ? `<div class="alert alert-danger">
          <strong>Validation Errors:</strong>
          ${this.scrollableDiv(this.formatErrorsList(data.errors_by_row))}
        </div>`
      : ''

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        <strong>${actionLabel} Failed</strong><br>
        ${data.message}
      </div>
      ${errorsSection}
    `
  }

  parseJson(response) {
    return response.json()
      .then(data => ({ response, data }))
      .catch(() => ({ response, data: {} }))
  }

  displayUploadError(errors) {
    if (!this.reportTarget) return

    this.reportTarget.innerHTML = `
      <div class="alert alert-danger">
        ${errors.join('<br>')}
      </div>
    `

    this.applyBtnTarget?.classList.add('d-none')
  }
}
